import { useEffect, useState } from 'react';
import Select from 'react-select';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/* -----------------------
   Condition options
----------------------- */
const CONDITION_OPTIONS = [
        { value: 'is', label: 'is' },
        { value: 'is_not', label: 'is not' },
        { value: 'contains', label: 'contains' },
        { value: 'does_not_contain', label: 'does not contain' }
];

const SourceFilterModal = ({
        isOpen,
        onClose,
        onApply,
        analytics
}) => {
        /* -----------------------
           Conditions
        ----------------------- */
        const [sourceCondition, setSourceCondition] = useState('is');
        const [channelCondition, setChannelCondition] = useState('is');
        const [referrerCondition, setReferrerCondition] = useState('is');

        /* -----------------------
           Multi-select values (ARRAYS) - used for ALL conditions now
        ----------------------- */
        const [source, setSource] = useState([]);
        const [channel, setChannel] = useState([]);
        const [referrer, setReferrer] = useState([]);

        /* -----------------------
           Options
        ----------------------- */
        const [sources, setSources] = useState([]);
        const [channels, setChannels] = useState([]);
        const [referrers, setReferrers] = useState([]);

        /* -----------------------
           Build options from analytics
        ----------------------- */
        useEffect(() => {
                if (!isOpen || !analytics) return;

                // Build source options from top_referrers
                if (analytics.top_referrers) {
                        const sourceSet = new Set();
                        analytics.top_referrers.forEach(ref => {
                                const refUrl = ref.referrer || ref.source;
                                if (refUrl && refUrl !== 'direct') {
                                        try {
                                                const url = new URL(refUrl);
                                                sourceSet.add(url.hostname.replace('www.', ''));
                                        } catch {
                                                sourceSet.add(refUrl);
                                        }
                                } else if (refUrl === 'direct') {
                                        sourceSet.add('Direct / None');
                                }
                        });
                        setSources(Array.from(sourceSet).map(s => ({ label: s, value: s })));
                        
                        // Also build referrer options (full URLs)
                        setReferrers(
                                analytics.top_referrers
                                        .filter(ref => ref.referrer || ref.source)
                                        .map(ref => ({
                                                label: ref.referrer || ref.source,
                                                value: ref.referrer || ref.source
                                        }))
                        );
                }

                // Build channel options - standard channels
                const standardChannels = [
                        'Direct',
                        'Organic Search',
                        'Paid Search',
                        'Social',
                        'Referral',
                        'Email',
                        'Affiliate',
                        'Display',
                        'Other'
                ];
                setChannels(standardChannels.map(c => ({ label: c, value: c })));

        }, [isOpen, analytics]);

        /* -----------------------
           Apply filters (f grammar)
        ----------------------- */
        const handleApply = () => {
                const fParams = [];

                // Source filter - all conditions use multi-select now
                if (source.length) {
                        fParams.push(
                                `${sourceCondition},source,${source.map(s => s.value).join('|')}`
                        );
                }

                // Channel filter
                if (channel.length) {
                        fParams.push(
                                `${channelCondition},channel,${channel.map(c => c.value).join('|')}`
                        );
                }

                // Referrer filter
                if (referrer.length) {
                        fParams.push(
                                `${referrerCondition},referrer,${referrer.map(r => r.value).join('|')}`
                        );
                }

                if (fParams.length) {
                        onApply(fParams);
                }

                onClose();
        };

        // Check if any filter has a value
        const hasValue = () => {
                return source.length > 0 || channel.length > 0 || referrer.length > 0;
        };

        // Reset state when modal closes
        useEffect(() => {
                if (!isOpen) {
                        setSource([]);
                        setChannel([]);
                        setReferrer([]);
                        setSourceCondition('is');
                        setChannelCondition('is');
                        setReferrerCondition('is');
                }
        }, [isOpen]);

        if (!isOpen) return null;

        return (
                <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
                        <div style={modalStyle}>
                                {/* Header */}
                                <div style={headerStyle}>
                                        <h3 style={titleStyle}>Filter by Source</h3>
                                        <button onClick={onClose} style={closeBtnStyle}>
                                                <X size={18} />
                                        </button>
                                </div>

                                <div style={dividerStyle} />

                                {/* Source */}
                                <FilterRow
                                        label="Source"
                                        condition={sourceCondition}
                                        setCondition={setSourceCondition}
                                        value={source}
                                        setValue={setSource}
                                        options={sources}
                                        placeholder="Select Source"
                                />

                                {/* Channel */}
                                <FilterRow
                                        label="Channel"
                                        condition={channelCondition}
                                        setCondition={setChannelCondition}
                                        value={channel}
                                        setValue={setChannel}
                                        options={channels}
                                        placeholder="Select Channel"
                                />

                                {/* Referrer URL */}
                                <FilterRow
                                        label="Referrer URL"
                                        condition={referrerCondition}
                                        setCondition={setReferrerCondition}
                                        value={referrer}
                                        setValue={setReferrer}
                                        options={referrers}
                                        placeholder="Select Referrer URL"
                                />

                                {/* Footer */}
                                <div style={footerStyle}>
                                        <Button
                                                onClick={handleApply}
                                                disabled={!hasValue()}
                                                style={{ backgroundColor: '#6b7280', color: '#fff' }}
                                        >
                                                Apply filter
                                        </Button>
                                </div>
                        </div>
                </div>
        );
};

/* -----------------------
   Reusable Filter Row - Always multi-select
----------------------- */
const FilterRow = ({
        label,
        condition,
        setCondition,
        value,
        setValue,
        options,
        placeholder
}) => (
        <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>{label}</label>

                <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Condition dropdown */}
                        <select
                                value={condition}
                                onChange={e => setCondition(e.target.value)}
                                style={selectStyle}
                        >
                                {CONDITION_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>
                                                {o.label}
                                        </option>
                                ))}
                        </select>

                        {/* Multi-select for all conditions */}
                        <div style={{ flex: 1 }}>
                                <Select
                                        options={options}
                                        value={value}
                                        onChange={val => setValue(val || [])}
                                        isMulti
                                        isClearable
                                        placeholder={placeholder}
                                        styles={reactSelectStyles}
                                />
                        </div>
                </div>
        </div>
);

/* -----------------------
   Styles
----------------------- */
const overlayStyle = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
};

const modalStyle = {
        width: '480px',
        background: '#fff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
};

const titleStyle = {
        margin: 0,
        fontSize: '16px',
        fontWeight: 600,
        color: '#0f172a',
        letterSpacing: '-0.01em'
};

const dividerStyle = {
        height: '1px',
        backgroundColor: '#f1f5f9',
        marginBottom: '20px'
};

const footerStyle = {
        marginTop: '20px',
        paddingTop: '16px',
        borderTop: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'flex-end'
};

const closeBtnStyle = {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#64748b',
        width: 28,
        height: 28,
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.15s, color 0.15s'
};

const labelStyle = {
        fontSize: '12px',
        fontWeight: 500,
        marginBottom: '8px',
        display: 'block',
        color: '#64748b'
};

const selectStyle = {
        padding: '10px 12px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        background: '#fff',
        color: '#0f172a',
        minWidth: '130px',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const reactSelectStyles = {
        control: (base, state) => ({
                ...base,
                minHeight: 40,
                fontSize: 14,
                borderRadius: 12,
                borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
                boxShadow: state.isFocused
                        ? '0 0 0 3px rgba(99, 102, 241, 0.12)'
                        : '0 1px 2px rgba(15, 23, 42, 0.04)',
                '&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
                transition: 'box-shadow 0.2s, border-color 0.2s'
        }),
        placeholder: (base) => ({ ...base, color: '#94a3b8' }),
        singleValue: (base) => ({ ...base, color: '#0f172a' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', padding: 6 }),
        menu: (base) => ({
                ...base,
                borderRadius: 12,
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
        }),
        option: (base, state) => ({
                ...base,
                fontSize: 13,
                color: '#0f172a',
                backgroundColor: state.isSelected ? '#eef2ff' : state.isFocused ? '#f8fafc' : 'transparent',
                cursor: 'pointer'
        }),
        multiValue: (base) => ({
                ...base,
                backgroundColor: '#eef2ff',
                borderRadius: 9999,
                paddingLeft: 4,
                paddingRight: 2
        }),
        multiValueLabel: (base) => ({
                ...base,
                color: '#4338ca',
                fontSize: 12,
                fontWeight: 500
        }),
        multiValueRemove: (base) => ({
                ...base,
                color: '#4338ca',
                borderRadius: 9999,
                ':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
        })
};

export default SourceFilterModal;
